import {
  Box,
  Heading,
  Stack,
  useToast,
  Spinner,
  VStack,
} from "@chakra-ui/react";
import { useKydEvent } from "../../hooks/useKydEvent";
import { matchPath, useLocation, useParams } from "react-router-dom";
import ReportsForm, { ReportMode } from "./ReportsForm";
import DownloadReportButton from "./DownloadReportButton";
import { useCallback, useMemo, useState } from "react";
import { useLoading } from "../../../hooks/form";
import { createOnUpdateEvent, EditModalType } from "../../../../utils";
import { pollForEventUpdateStatus as pollForStatus } from "../../utils/poolForEventUpdateStatus";
import { useDialog } from "../../../../Common/Dialog";
import actions from "../../../../actions";
import TicketSalesReportingSection from "./TicketSalesReportingSection";
import EventDetailSections from "../../sections/EventDetailSections";
import { KYDReportDay } from "@common/interfaces/KYDEvent";

const ReportingNavSection = () => {
  const { event_id } = useParams();
  const location = useLocation();
  const { kydEvent, loading: isEventLoading, refetch } = useKydEvent(event_id);
  const [modalType, setModalType] = useState(EditModalType.INACTIVE);
  const [loading, updateLoading] = useLoading({
    event: false,
  });
  const dialog = useDialog();
  const toast = useToast();

  const currentNavTabSections = useMemo(() => {
    if (!kydEvent) return [];

    const currentTab = kydEvent.nav.find((nav) =>
      matchPath(nav.url, location.pathname)
    );
    if (currentTab) {
      return currentTab.sections;
    }

    return [];
  }, [kydEvent]);

  const pollForEventUpdateStatus = useCallback(() => {
    return pollForStatus({
      onError: (text) => dialog({ text }),
      refetch: () => refetch({ silent: true }),
      event_id,
    });
  }, [event_id, dialog, refetch]);

  const onUpdateEvent = useCallback(
    createOnUpdateEvent({
      dialog,
      event_id,
      pollForEventUpdateStatus,
      refetch,
      setLoading: (isLoading) => updateLoading("event", isLoading),
      setModalType,
      toast,
    }),
    [
      event_id,
      refetch,
      updateLoading,
      pollForEventUpdateStatus,
      dialog,
      toast,
      setModalType,
    ]
  );

  const onEditReportRecipients = async ({
    mode,
    recipients,
    custom_days,
    report_time,
  }: {
    mode: ReportMode;
    recipients: string[];
    custom_days: KYDReportDay[];
    report_time: string;
  }) => {
    try {
      await actions.ADMIN.createReportRecipients({
        event_id: kydEvent.id,
        mode,
        recipients,
        custom_days,
        report_time,
      });
      refetch();
      toast({
        status: "success",
        title: "Ticket reports updated!",
        description: "Ticket reports for event were updated",
      });
    } catch (e) {
      console.log("Error creating recipients", e);
    }
  };

  return (
    <Stack>
      {!kydEvent?.nav ? (
        <VStack w="100%">
          <Spinner />
        </VStack>
      ) : null}
      <Stack
        direction={{ base: "column", md: "row" }}
        justify={"space-between"}
        w="100%"
      >
        <Heading w="full" size="md">
          Reporting
        </Heading>
      </Stack>
      <EventDetailSections
        isLoading={isEventLoading || loading.event}
        onUpdateEvent={onUpdateEvent}
        setModalType={setModalType}
        refetchEvent={refetch}
        modalType={modalType}
        sections={currentNavTabSections}
        pollForEventUpdateStatus={pollForEventUpdateStatus}
      />
      <Stack py={4} overflowX={"scroll"}>
        <DownloadReportButton
          title="Sales Report"
          url={kydEvent?.reports?.tickets?.url}
        />
        <DownloadReportButton
          title="Settlement Report"
          url={kydEvent?.reports?.settlement_pdf?.url}
        />
        <ReportsForm kydEvent={kydEvent} onSubmit={onEditReportRecipients} />
      </Stack>
    </Stack>
  );
};

export default ReportingNavSection;
